import { useEffect, useState } from "react";
import { ApiError, api, formatBirth, formatBirthLong, zodiacSign, type AppConfig, type Goal, type LifeStatus, type Priority, type UserView } from "../api";
import { openTelegramLink, deviceTimezone } from "../platform";
import { DateField, Overlay, TIER_TITLE, formatUntil, isFullDate } from "../ui";

/** «Мой архетип»: достаём имя программы личности из уже посчитанного разбора «Звезда» (headline вида «... — N: Имя»). */
function useArchetype(): string | null {
  const [name, setName] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    api
      .reading("star")
      .then((r) => {
        if (!active) return;
        const section = r.reading.doc.sections.find((s) => s.id.includes("personality") && s.heading);
        const m = section?.heading ? /:\s*([^:]+)$/u.exec(section.heading) : null;
        setName(m?.[1]?.trim() ?? null);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);
  return name;
}

const LIFE_STATUS_LABELS: Record<LifeStatus, string> = {
  free: "Свободен(на)",
  in_love: "Влюблён(а)",
  in_relationship: "В отношениях",
  complicated: "Всё сложно",
};
const PRIORITY_LABELS: Record<Priority, string> = {
  love: "Любовь и отношения",
  money: "Деньги и работа",
  self: "Я сам(а): мой путь",
  all: "Всё сразу",
};
/** Те же значения goal, что и в онбординге (Birth.tsx PAIN_OPTIONS) — держать формулировки одинаковыми. */
const GOAL_LABELS: Record<Goal, string> = {
  self: "Не понимаю, чего хочу и куда двигаться",
  relationships: "Сложности в отношениях",
  money: "Нестабильность с деньгами",
  gradually: "Всего понемногу, не могу выделить главное",
};

export function Profile({
  user,
  config,
  birthDateChangeEnabled,
  onClose,
  onPaywall,
  onUser,
  onRefresh,
  onReferral,
  showToast,
}: {
  user: UserView;
  config: AppConfig | null;
  birthDateChangeEnabled: boolean;
  onClose: () => void;
  onPaywall: () => void;
  onUser: (u: UserView) => void;
  onRefresh: () => void;
  onReferral: () => void;
  showToast: (t: string) => void;
}) {
  const refLink = config?.botUsername ? `https://t.me/${config.botUsername}?start=ref_${user.referralCode}` : null;
  const archetype = useArchetype();
  const sign = zodiacSign(user.birthDate);

  const [editingTime, setEditingTime] = useState(false);
  const [timeValue, setTimeValue] = useState(user.birthTime ?? "");
  const [editingCity, setEditingCity] = useState(false);
  const [cityValue, setCityValue] = useState(user.birthCity ?? "");

  const saveBirthTime = async () => {
    if (!/^([01]\d|2[0-3]):[0-5]\d$/u.test(timeValue)) {
      showToast("Формат времени — ЧЧ:ММ");
      return;
    }
    try {
      const r = await api.settings({ birthTime: timeValue });
      onUser(r.user);
      setEditingTime(false);
    } catch {
      showToast("Не удалось сохранить время рождения");
    }
  };

  const saveBirthCity = async () => {
    if (!cityValue.trim()) return;
    try {
      const r = await api.settings({ birthCity: cityValue.trim() });
      onUser(r.user);
      setEditingCity(false);
    } catch {
      showToast("Не удалось сохранить город рождения");
    }
  };

  const [editingDate, setEditingDate] = useState(false);
  const [dateValue, setDateValue] = useState("");
  const [dateBusy, setDateBusy] = useState(false);
  const [dateError, setDateError] = useState<string | null>(null);

  const submitDate = async () => {
    setDateBusy(true);
    setDateError(null);
    try {
      const r = await api.setBirth(dateValue, deviceTimezone());
      onUser(r.user);
      onRefresh();
      setEditingDate(false);
      setDateValue("");
      showToast("Дата расчёта изменена");
    } catch (e) {
      setDateError(
        e instanceof ApiError && e.status === 400
          ? "Не получилось разобрать дату. Проверьте день, месяц и год."
          : "Не удалось сохранить. Попробуйте ещё раз.",
      );
    } finally {
      setDateBusy(false);
    }
  };

  const togglePush = async () => {
    try {
      const r = await api.settings({ dailyPush: !user.dailyPush });
      onUser(r.user);
    } catch {
      showToast("Не удалось сохранить настройку");
    }
  };

  const updateProfileAnswer = async (field: "lifeStatus" | "priority" | "goal", value: string) => {
    try {
      const r = await api.settings({ [field]: value } as Parameters<typeof api.settings>[0]);
      onUser(r.user);
    } catch {
      showToast("Не удалось сохранить");
    }
  };

  const copy = async () => {
    if (!refLink) return;
    try {
      await navigator.clipboard.writeText(refLink);
      showToast("Ссылка скопирована");
    } catch {
      showToast(refLink);
    }
  };

  return (
    <Overlay title="Профиль" onClose={onClose} tone="light">
      <div className="sheetbody">
        <p className="profile-kicker">Я</p>
        <h2 className="profile-name">
          {user.firstName ?? "Без имени"}
          {sign ? ` · ${sign.name}` : ""}
        </h2>
        <div className="profile-dual">
          <div className="profile-dual-card">
            <div className="profile-dual-symbol">{sign?.symbol ?? "✶"}</div>
            <p className="profile-dual-label">Мой портрет</p>
            <p className="profile-dual-value">{sign?.name ?? "—"}</p>
          </div>
          <div className="profile-dual-card">
            <div className="profile-dual-symbol">✦</div>
            <p className="profile-dual-label">Мой архетип</p>
            <p className="profile-dual-value">{archetype ?? "—"}</p>
          </div>
        </div>
        <button className="profile-sub-row" onClick={onPaywall}>
          <span>Моя подписка</span>
          <span className="profile-sub-row-value">
            {TIER_TITLE[user.tier]}
            {user.tierExpiresAt ? ` до ${formatUntil(user.tierExpiresAt, user.timezone)}` : ""} →
          </span>
        </button>
        <div className="card">
          <h3>Обо мне</h3>
          <div className="row">
            <span>Дата рождения</span>
            <b>{formatBirthLong(user.birthDate)}</b>
          </div>
          <div className="row">
            <span>Знак</span>
            <b>{sign?.name ?? "—"}</b>
          </div>
          <div className="row">
            <span>Время рождения</span>
            {editingTime ? (
              <span className="row-edit">
                <input className="row-edit-input" type="time" value={timeValue} onChange={(e) => setTimeValue(e.target.value)} autoFocus />
                <button className="row-edit-save" onClick={() => void saveBirthTime()}>
                  ✓
                </button>
              </span>
            ) : (
              <button className="row-link" onClick={() => setEditingTime(true)}>
                {user.birthTime ?? "Добавить"}
              </button>
            )}
          </div>
          <div className="row">
            <span>Город рождения</span>
            {editingCity ? (
              <span className="row-edit">
                <input
                  className="row-edit-input"
                  type="text"
                  value={cityValue}
                  placeholder="Город"
                  onChange={(e) => setCityValue(e.target.value)}
                  autoFocus
                />
                <button className="row-edit-save" onClick={() => void saveBirthCity()}>
                  ✓
                </button>
              </span>
            ) : (
              <button className="row-link" onClick={() => setEditingCity(true)}>
                {user.birthCity ?? "Добавить"}
              </button>
            )}
          </div>
        </div>
        <div className="card">
          <h3>Доступ: {TIER_TITLE[user.tier]}</h3>
          <p className="sub">
            {user.tierExpiresAt ? `до ${formatUntil(user.tierExpiresAt, user.timezone)}` : "Без ограничения по времени"}
            {user.birthDate ? ` · дата рождения ${formatBirth(user.birthDate)}` : ""}
          </p>
          <button className="primary" onClick={onPaywall}>
            {user.tier === "free" ? "Выбрать тариф" : "Продлить или улучшить"}
          </button>
          {birthDateChangeEnabled && !editingDate && (
            <button className="outline" style={{ marginTop: 8, width: "100%" }} onClick={() => setEditingDate(true)}>
              Рассчитать на другую дату
            </button>
          )}
          {birthDateChangeEnabled && editingDate && (
            <div className="birth" style={{ marginTop: 8 }}>
              <DateField label="Дата для расчёта" value={dateValue} onChange={setDateValue} autoFocus />
              {dateError && <div className="error">{dateError}</div>}
              <div className="row" style={{ gap: 8 }}>
                <button className="outline" onClick={() => { setEditingDate(false); setDateError(null); }}>
                  Отмена
                </button>
                <button className="primary" disabled={!isFullDate(dateValue) || dateBusy} onClick={() => void submitDate()}>
                  {dateBusy ? "Считаем…" : "Рассчитать"}
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="card">
          <div className="row">
            <span>🔥 Серия дней</span>
            <b>{user.streak}</b>
          </div>
          <div className="row">
            <span className="row-key">
              <img src="/assets/icons/key.webp" alt="" className="key-icon" />
              Ключи
            </span>
            <b>{user.coins}</b>
          </div>
          <div className="row">
            <span>Утреннее «число дня» в 09:00</span>
            <button
              className={`switch${user.dailyPush ? " on" : ""}`}
              onClick={() => void togglePush()}
              aria-pressed={user.dailyPush}
              aria-label="Утреннее сообщение"
            />
          </div>
        </div>
        <div className="card">
          <h3>О тебе</h3>
          <p className="pill-group-label">Личная жизнь</p>
          <div className="pill-grid">
            {(Object.entries(LIFE_STATUS_LABELS) as [LifeStatus, string][]).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`pill-choice${user.lifeStatus === id ? " on" : ""}`}
                onClick={() => void updateProfileAnswer("lifeStatus", id)}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="pill-group-label">Что сейчас важнее</p>
          <div className="pill-grid">
            {(Object.entries(PRIORITY_LABELS) as [Priority, string][]).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`pill-choice${user.priority === id ? " on" : ""}`}
                onClick={() => void updateProfileAnswer("priority", id)}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="pill-group-label">Что сейчас беспокоит</p>
          <div className="pill-grid">
            {(Object.entries(GOAL_LABELS) as [Goal, string][]).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`pill-choice${user.goal === id ? " on" : ""}`}
                onClick={() => void updateProfileAnswer("goal", id)}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="sub" style={{ marginTop: 6 }}>Учту в следующих раскладах и в карте дня.</p>
        </div>
        {refLink && (
          <div className="card">
            <h3>Пригласить друга</h3>
            <p className="sub">Друг получает ключи за вход, вы — за его активность и первую оплату тарифа.</p>
            <div className="row" style={{ gap: 8 }}>
              <button className="outline" style={{ flex: 1 }} onClick={() => void copy()}>
                Скопировать ссылку
              </button>
              <button className="outline" style={{ flex: 1 }} onClick={onReferral}>
                Подробнее →
              </button>
            </div>
          </div>
        )}
        <div className="notes">
          {config?.termsUrl && (
            <p>
              <a href={config.termsUrl}>Условия и политика данных</a>
            </p>
          )}
          {config?.supportUrl && (
            <p>
              <button className="sub" onClick={() => openTelegramLink(config.supportUrl!)}>
                💬 Поддержка
              </button>
            </p>
          )}
          <p>Удалить свои данные можно командой /delete_data в боте.</p>
        </div>
      </div>
    </Overlay>
  );
}
